import React, { useState, useCallback, useRef } from 'react';
import PropTypes from 'prop-types';
import { useTheme } from '@mui/material/styles';
import Avatar from '@mui/material/Avatar';
import Chip from '@mui/material/Chip';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemAvatar from '@mui/material/ListItemAvatar';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import CircularProgress from '@mui/material/CircularProgress';
import { ThemeMode } from 'config';
import { getTypeColor, getTypeTextColor, getTypeIcons } from '../../../../store/constant';
import { IconMapPin, IconChevronDown, IconChevronRight } from '@tabler/icons-react';
import TruncateTooltip from '../../../../commonComponent/TruncateTooltip';

const CHILD_LIST_MAX_HEIGHT = 220;

function NotificationList({ filter = 'all', notifications = [], onNotificationClick, onFetchCorrelatedAlarms }) {
  const theme = useTheme();
  const filteredNotifications = filter === 'all' ? notifications : notifications.filter((n) => n.status === filter);

  const [expandedIds, setExpandedIds] = useState({});
  const [childData, setChildData] = useState({});
  const scrollRefs = useRef({});

  const handleNotificationClick = useCallback(() => {
    if (onNotificationClick) onNotificationClick();
  }, [onNotificationClick]);

  const getNotificationIcon = () => <IconMapPin stroke={1.3} />;

  const toggleExpand = useCallback(
    async (e, notification) => {
      e.stopPropagation();
      if (!notification?.correlatable || !notification?.correlationId) return;
      const id = notification.id;
      const isExpanding = !expandedIds[id];
      setExpandedIds((prev) => ({ ...prev, [id]: isExpanding }));

      if (isExpanding && onFetchCorrelatedAlarms) {
        const existing = childData[id];
        if (existing?.items?.length > 0) return;
        setChildData((prev) => ({ ...prev, [id]: { ...prev[id], loading: true } }));
        try {
          const { items, hasMore } = await onFetchCorrelatedAlarms(notification.correlationId, 0);
          setChildData((prev) => ({
            ...prev,
            [id]: { items, page: 0, hasMore, loading: false }
          }));
        } catch (err) {
          console.error('Failed to fetch correlated alarms', err);
          setChildData((prev) => ({
            ...prev,
            [id]: { items: [], page: 0, hasMore: false, loading: false }
          }));
        }
      }
    },
    [expandedIds, childData, onFetchCorrelatedAlarms]
  );

  const loadMoreChildren = useCallback(
    async (parentId, correlationId) => {
      const data = childData[parentId];
      if (!data?.hasMore || data.loading || !onFetchCorrelatedAlarms) return;
      setChildData((prev) => ({
        ...prev,
        [parentId]: { ...prev[parentId], loading: true }
      }));
      try {
        const nextPage = (data.page ?? 0) + 1;
        const { items: newItems, hasMore } = await onFetchCorrelatedAlarms(correlationId, nextPage);
        setChildData((prev) => ({
          ...prev,
          [parentId]: {
            items: [...(prev[parentId]?.items || []), ...newItems],
            page: nextPage,
            hasMore,
            loading: false
          }
        }));
      } catch (err) {
        console.error('Failed to load more correlated alarms', err);
        setChildData((prev) => ({
          ...prev,
          [parentId]: { ...prev[parentId], loading: false }
        }));
      }
    },
    [childData, onFetchCorrelatedAlarms]
  );

  const handleChildScroll = useCallback(
    (e, parentId, correlationId) => {
      const el = e.target;
      if (!el || !childData[parentId]?.hasMore || childData[parentId]?.loading) return;
      const { scrollTop, scrollHeight, clientHeight } = el;
      if (scrollHeight - scrollTop - clientHeight < 60) {
        loadMoreChildren(parentId, correlationId);
      }
    },
    [childData, loadMoreChildren]
  );

  const renderItemContent = (notification, isChild = false) => (
    <>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'flex-start',
          gap: 2,
          width: '100%',
          mb: 1.5,
          pb: 1,
          borderBottom: '1px solid',
          borderColor: theme.palette.divider
        }}
      >
        {!isChild && (
          <ListItemAvatar sx={{ minWidth: 'auto' }}>
            <Avatar
              sx={{
                bgcolor: notification?.status === 'unread' ? theme.palette.background.default : 'grey.100',
                border: '1px solid',
                borderColor: theme.palette.divider
              }}
            >
              <Typography color={theme.palette.mode === ThemeMode.DARK ? theme.palette.grey[600] : 'grey.900'} sx={{ lineHeight: '5px' }}>
                {getNotificationIcon()}
              </Typography>
            </Avatar>
          </ListItemAvatar>
        )}
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography
            variant="body2"
            sx={{
              fontWeight: notification?.status === 'unread' ? 500 : 400,
              mb: 0.5,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              maxWidth: isChild ? '280px' : '310px',
              color: 'grey.900'
            }}
          >
            {notification?.title}
          </Typography>
          <Typography variant="body1" sx={{ color: 'grey.600' }}>
            {notification?.time}
          </Typography>
        </Box>
        <Chip
          label={notification?.type}
          size="small"
          icon={getTypeIcons(notification.type, theme)}
          sx={{
            bgcolor: getTypeColor(notification.type, theme),
            color: getTypeTextColor(notification?.type, theme),
            alignSelf: 'center',
            '& .MuiChip-label': { px: 1 },
            borderRadius: 1.5,
            padding: '5px 3px',
            height: 'auto',
            border: 'none'
          }}
        />
      </Box>
      <TruncateTooltip sx={{ maxWidth: '100%', color: 'grey.800' }}>{notification?.message}</TruncateTooltip>
    </>
  );

  if (filteredNotifications.length === 0) {
    return (
      <List sx={{ width: '100%', py: 0 }}>
        <Box
          sx={{
            py: 4,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            minHeight: { xs: '250px', sm: '350px', md: '450px' }
          }}
        >
          <Typography variant="body1" align="center" color="text.secondary" sx={{ fontSize: '1rem' }}>
            No New Alarms
          </Typography>
        </Box>
      </List>
    );
  }

  return (
    <List sx={{ width: '100%', py: 0 }}>
      {filteredNotifications.map((notification) => {
        const id = notification?.id;
        const isExpanded = expandedIds[id];
        const children = childData[id];
        const showChevron = Boolean(notification?.correlatable && notification?.correlationId);

        return (
          <React.Fragment key={id}>
            <ListItem
              onClick={() => handleNotificationClick(id)}
              sx={{
                mb: 2,
                borderRadius: 3,
                border: '1px solid',
                cursor: 'pointer',
                p: 2,
                borderColor: theme.palette.divider,
                backgroundColor:
                  notification?.status === 'unread'
                    ? theme.palette.mode === 'dark'
                      ? theme.palette.background.default
                      : theme.palette.grey[50]
                    : theme.palette.background.paper,
                flexDirection: 'column',
                alignItems: 'flex-start',
                '&:last-child': { mb: 0 }
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'flex-start', width: '100%' }}>
                {showChevron && (
                  <IconButton
                    size="small"
                    onClick={(e) => toggleExpand(e, notification)}
                    sx={{ mr: 0.5, p: 0.25 }}
                    aria-label={isExpanded ? 'Collapse' : 'Expand'}
                  >
                    {isExpanded ? <IconChevronDown stroke={1.5} size={18} /> : <IconChevronRight stroke={1.5} size={18} />}
                  </IconButton>
                )}
                <Box sx={{ flex: 1, minWidth: 0 }}>{renderItemContent(notification, false)}</Box>
              </Box>

              {showChevron && isExpanded && (
                <Box
                  sx={{
                    width: '100%',
                    pl: showChevron ? 3.5 : 0,
                    mt: 1,
                    borderLeft: '2px solid',
                    borderColor: theme.palette.divider,
                    borderRadius: 1
                  }}
                >
                  <Box
                    ref={(el) => {
                      scrollRefs.current[id] = el;
                    }}
                    onScroll={(e) => handleChildScroll(e, id, notification.correlationId)}
                    sx={{
                      maxHeight: CHILD_LIST_MAX_HEIGHT,
                      overflowY: 'auto',
                      '&::-webkit-scrollbar': { width: 5 }
                    }}
                  >
                    {children?.loading && !children?.items?.length ? (
                      <Box sx={{ py: 2, display: 'flex', justifyContent: 'center' }}>
                        <CircularProgress size={24} />
                      </Box>
                    ) : (
                      (children?.items || []).map((child) => (
                        <Box
                          key={child?.id}
                          sx={{
                            py: 1.5,
                            px: 1.5,
                            mb: 1,
                            borderRadius: 2,
                            border: '1px solid',
                            borderColor: theme.palette.divider,
                            backgroundColor: theme.palette.mode === 'dark' ? 'grey.900' : 'grey.50'
                          }}
                        >
                          {renderItemContent(child, true)}
                        </Box>
                      ))
                    )}
                    {children?.hasMore && children?.loading && children?.items?.length > 0 && (
                      <Box sx={{ py: 1, display: 'flex', justifyContent: 'center' }}>
                        <CircularProgress size={20} />
                      </Box>
                    )}
                  </Box>
                </Box>
              )}
            </ListItem>
          </React.Fragment>
        );
      })}
    </List>
  );
}

NotificationList.propTypes = {
  filter: PropTypes.string,
  notifications: PropTypes.array,
  onNotificationClick: PropTypes.func,
  onFetchCorrelatedAlarms: PropTypes.func
};

export default NotificationList;
